import { useContext, useEffect, useRef, useState } from 'react';

import { getDataServiceUrl } from '@amzn/awsboost-ui-common/lib/bpdsWebAppConfig/';
import { HttpClient } from '@amzn/awsboost-ui-common/lib/http';

import { ASTNode, print } from 'graphql';

import { logger } from '../appSelectorContext';
import { BOOST_CLIENT_NAME } from '../boost/utils';
import { GET_EXECUTION_STATUS } from '../components/ExecutionStatus/GetExecutionStatusService';
import { SEARCH_MODELS, SEARCH_PO_ASNS, SUGGEST_MODELS } from '../components/MPNsToReceive/MPNsToReceiveService';
import { GET_PERMISSIONS } from '../components/Permissions/GetPermissionsService';
import { RECEIVE_PARTS } from '../components/ReceivePartsAction/ReceivePartsService';
import { CONTAINER_SUGGESTIONS, VALIDATE_CONTAINER } from '../components/ScanContainer/ScanContainerService';
import { VALIDATE_SERIAL, VALIDATE_SERIALS } from '../components/ScanSerials/ScanSerialService';
import { GET_SITES } from '../components/SiteInput/GetSitesService';
import { SEARCH_REFERENCES } from '../components/StartReceiving/StartReceivingService';
import { GET_TAGS_CONFIGURATION } from '../components/Tags/GetTagsConfigurationService';
import { AppContext } from '../store/appContext';
import useLocalStorage from './useLocalStorage';

export type FetchPolicy = 'cache-first' | 'network-only' | 'cache-only' | 'no-cache' | 'standby';

// while running unit tests disable midway credentials
export const isTest = process.env.NODE_ENV === 'test';

const DEFAULT_OPTIONS = {
  method: 'POST', // defaulting to POST because most of Receive services are supporting POST requests
  headers: {
    'Content-Type': 'application/json',
  },
};

export const SERVICE_QUERIES = {
  SEARCH_REFERENCES: 'PREPROCESSOR_QUERY',
  EXECUTION_QUERY: 'EXECUTION_QUERY',
};
// TBD - configure for prod and zhy environments
const boostPlatformUrl = getDataServiceUrl(window, 'platform').replace('/graphql', '');

export const EXCEPTION_URL_MAPPING = {
  appConfig: `${boostPlatformUrl}/proxy/receive-exception-api-pdx/receive-exception/exception-type-to-resolver-group/list`,
  create: `${boostPlatformUrl}/proxy/receive-exception-api-pdx/receive-exception/create`,
};

export const BOOST_EXECUTION_URLS = {
  metadata: `${boostPlatformUrl}/proxy/receive-execution-api-pdx/receive/boost-work-request/update`,
};

// TODO move these changes to preprocessor once dceo changes are finalized
export const VALIDATE_ASSET_TAG = {
  assetTag: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/validate/yellow-asset-tag`,
};

export const SERVICE_NAME_URL_MAPPING: any = {
  // Get sites
  [print(GET_SITES)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/get/sites`,

  // Get permissions for IA
  [print(
    GET_PERMISSIONS,
  )]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/get/permissions/inventory-adjustment`,

  // search references
  [print(SEARCH_REFERENCES)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/search/reference`,

  // search mpn references

  [print(SEARCH_MODELS)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/list/reference-line-items`,
  [print(SEARCH_PO_ASNS)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/get/asn-references`,
  [print(SUGGEST_MODELS)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/get/mpnByPrefix`,

  [print(GET_TAGS_CONFIGURATION)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/get/configuration/tags`,

  // search containers
  [print(CONTAINER_SUGGESTIONS)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/get/container-suggestions`,
  [print(VALIDATE_CONTAINER)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/validate/container-metadata`,

  [print(VALIDATE_SERIALS)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/batch/validate/serial-metadata`,

  [print(VALIDATE_SERIAL)]: `${boostPlatformUrl}/proxy/receive-preprocessor-api-pdx/validate/serial-metadata`,

  // Executions
  [print(RECEIVE_PARTS)]: `${boostPlatformUrl}/proxy/receive-execution-api-pdx/receive/receive-material/async`,
  [print(GET_EXECUTION_STATUS)]: `${boostPlatformUrl}/proxy/receive-execution-api-pdx/receive/get-status/executionId`,
};

export const NormalizeResponseReferenceKeys = {
  [print(GET_SITES)]: 'getSites',
  [print(GET_PERMISSIONS)]: 'getPermissions',
  [print(SEARCH_REFERENCES)]: 'searchReferences',
  [print(SEARCH_MODELS)]: 'listReferenceLineItems',
  [print(SEARCH_PO_ASNS)]: 'getASNReferences',
  [print(SUGGEST_MODELS)]: 'getMpnAutoCorrection',
  [print(CONTAINER_SUGGESTIONS)]: 'getContainerSuggestions',
  [print(VALIDATE_CONTAINER)]: 'validateContainerMetadata',
  [print(VALIDATE_SERIALS)]: 'validateSerialsMetadata',
  [print(VALIDATE_SERIAL)]: 'validateSerialMetadata',
  [print(RECEIVE_PARTS)]: 'receiveMaterialAsync',
  [print(GET_EXECUTION_STATUS)]: 'getReceiveStatusByExecutionId',
  [print(GET_TAGS_CONFIGURATION)]: 'getTagsConfiguration',
};

export const MapHttpMethodResponseReferenceKeys = {
  [print(GET_SITES)]: 'POST',
  [print(GET_PERMISSIONS)]: 'POST',
  [print(SEARCH_REFERENCES)]: 'POST',
  [print(SEARCH_MODELS)]: 'POST',
  [print(SEARCH_PO_ASNS)]: 'POST',
  [print(SUGGEST_MODELS)]: 'POST',
  [print(CONTAINER_SUGGESTIONS)]: 'POST',
  [print(VALIDATE_CONTAINER)]: 'POST',
  [print(VALIDATE_SERIALS)]: 'POST',
  [print(VALIDATE_SERIAL)]: 'POST',
  [print(RECEIVE_PARTS)]: 'POST',
  [print(GET_EXECUTION_STATUS)]: 'POST',
};

export function httpFetch(url: string, options: any, queryKey?: string) {
  if (!window.awsBoostClient) {
    window.awsBoostClient = new HttpClient({ midwayEnabled: !isTest });
    console.log('window.awsBoostClient instance', window.awsBoostClient);
  }
  const body = options.body;
  return window.awsBoostClient.post(url, body, { headers: options.headers });
}

// normalize HTTP response be in sync with existing GQL response
function normalizeHTTPResponse(response: any, refKey: string, variables?: any) {
  if (refKey === print(VALIDATE_SERIALS)) {
    return {
      [NormalizeResponseReferenceKeys[refKey]]: response.serials,
    };
  }

  return {
    [NormalizeResponseReferenceKeys[refKey]]: { ...response, variables },
  };
}

function normalizeHTTPRequest(request: any, refKey: string, appContext?: any) {
  let updatedRequest = {
    ...request,
    clientName: BOOST_CLIENT_NAME,
    clientWorkRequestId: appContext.context.workRequestId,
  };
  if (refKey === 'getReceiveStatusByExecutionId') {
    updatedRequest = {
      ...updatedRequest,
      receiveExecutionUUId: request.receiveExecutionUuId,
    };
  }

  if (refKey === 'receiveMaterialAsync' || refKey === 'searchReferences') {
    updatedRequest = {
      ...updatedRequest,
      shipperPackageId: appContext.context.packageId,
    };
  }

  return updatedRequest;
}

let httpQueryMapObject: any = {};
let timeIntervalInstances: any = {};

export const useHttpQuery = (QUERY: ASTNode, { onCompleted, onError, fetchPolicy, pollInterval }: any): any => {
  const queryKey = print(QUERY);
  const queryReferenceKey = NormalizeResponseReferenceKeys[queryKey];
  const appContext = useContext(AppContext);
  const [, updateHttpStore] = useLocalStorage('httpCache', null);

  const variablesRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [called, setCalled] = useState(false);
  const [data, setData] = useState<null | unknown>(null);
  const [error, setError] = useState<null | unknown>(null);

  const URL = SERVICE_NAME_URL_MAPPING[queryKey];

  if (!URL) {
    throw new Error(`No URL mapping found for query ${queryKey}`);
  }

  const getQuery = () => {
    return httpQueryMapObject;
  };

  const getQueryByReferenceKey = (queryReferenceKey: string) => {
    if (!httpQueryMapObject[queryReferenceKey]) {
      console.error('no reference key available', queryReferenceKey);
      return null;
    }
    return httpQueryMapObject[queryReferenceKey];
  };

  const setQuery = (updatedQuery: unknown) => {
    httpQueryMapObject = {
      ...httpQueryMapObject,
      [queryReferenceKey]: updatedQuery,
    };
    updateHttpStore('httpCache', httpQueryMapObject);
  };

  const stopPolling = (variables: unknown) => {
    console.log('stopPolling: timeIntervalInstances', variables, queryReferenceKey, timeIntervalInstances);
    const httpQueryMap = getQueryByReferenceKey(queryReferenceKey);
    const existingInstances = timeIntervalInstances[JSON.stringify(variables)];

    // step to make sure to clear any instances which were left open
    if (Array.isArray(existingInstances) && existingInstances?.length > 0) {
      existingInstances.forEach((timerIterval) => {
        clearInterval(timerIterval);
        timeIntervalInstances[JSON.stringify(variables)] = timeIntervalInstances[JSON.stringify(variables)].filter(
          (instance) => instance !== timerIterval,
        );
      });
    }

    if (!httpQueryMap?.[JSON.stringify(variables)].timeIntervalInstance) {
      return;
    }

    const updatedQuery = {
      ...httpQueryMap,
      [JSON.stringify(variables)]: {
        ...httpQueryMap?.[JSON.stringify(variables)],
        timeIntervalInstance: null,
        polling: false,
      },
    };

    clearInterval(httpQueryMap?.[JSON.stringify(variables)].timeIntervalInstance);
    setQuery(updatedQuery);
  };

  const httpQueryMap = getQueryByReferenceKey(queryReferenceKey);

  const getVariableValues = () => {
    return variablesRef.current;
  };

  const queryResponse = {
    loading,
    variables: getVariableValues(),
    error,
    data,
    stopPolling,
    called,
    polling: httpQueryMap?.polling || false,
  };

  const onRegisterPolling = (variables: any, timeIntervalInstance?: any) => {
    const httpQueryMap = getQueryByReferenceKey(queryReferenceKey);

    if (httpQueryMap?.[JSON.stringify(variables)].polling) {
      logger.log('timeoutInstance already registed for ', queryReferenceKey);
      return;
    }

    const updatedQuery = {
      ...httpQueryMap,
      [JSON.stringify(variables)]: {
        ...httpQueryMap?.[JSON.stringify(variables)],
        timeIntervalInstance,
        polling: true,
      },
    };

    setQuery({
      ...updatedQuery,
    });
  };

  const processNetworkRequest = async (props: { variables: any }) => {
    setLoading(true);
    queryResponse.variables = props.variables;
    const request = normalizeHTTPRequest(props.variables, queryReferenceKey, appContext);

    try {
      const response = await httpFetch(URL, {
        method: MapHttpMethodResponseReferenceKeys[queryKey],
        headers: {
          'Content-Type': 'application/json',
        },
        body: request,
      });
      const normalizedResponse = normalizeHTTPResponse(response, queryKey, props.variables);
      updateHttpQuery(normalizedResponse, props.variables);

      setData(normalizedResponse);
      onCompleted?.(normalizedResponse);
    } catch (error) {
      const graphQLErrors = [
        {
          extensions: {
            exception: {
              code: error?.content?.serverErrorCode,
              message: error?.content?.error?.message,
            },
          },
        },
      ];

      setError({ graphQLErrors });
      onError?.({ graphQLErrors });
      updateHttpQuery(null, props.variables, graphQLErrors);
    } finally {
      setLoading(false);
    }
  };

  const updateHttpQuery = (response: any, variables: any, error?: any) => {
    const existingQuery = getQueryByReferenceKey(queryReferenceKey)?.[JSON.stringify(variables)] || {};
    console.log('updating cache response', existingQuery, response, error, variables);
    const queryRequest = {
      [JSON.stringify(variables)]: {
        ...existingQuery,
        response,
        pending: false,
        endTime: Date.now(),
        error,
      },
    };

    setQuery({ ...getQueryByReferenceKey(queryReferenceKey), ...queryRequest });
  };

  const processRequest = (variables: any, onRegisterPolling?: (variables: any, intervalTimeout: any) => void) => {
    const queryEntry = getQuery()[queryReferenceKey]?.[JSON.stringify(variables)];
    const isPollingEnabled = pollInterval && typeof pollInterval === 'number' && pollInterval > 0;
    const isPollingActive = queryEntry?.polling;

    variablesRef.current = variables;
    console.log('isPollingEnabled', isPollingEnabled, isPollingActive);
    if (isPollingEnabled && !isPollingActive) {
      startPolling(variables, onRegisterPolling);
    } else {
      processNetworkRequest({ variables });
    }
  };

  const startPolling = (variables: any, onRegisterPolling?: (variables: any, intervalTimeout: any) => void) => {
    const timeIntervalInstance = setInterval(() => {
      console.log('polling', getQuery());
      processNetworkRequest({ variables });
    }, pollInterval);

    const existingInstances = timeIntervalInstances[JSON.stringify(variables)] || [];
    timeIntervalInstances = {
      ...timeIntervalInstances,
      [JSON.stringify(variables)]: existingInstances.concat(timeIntervalInstance),
    };

    onRegisterPolling?.(variables, timeIntervalInstance);
  };

  const handleQuery = (params: any, options: any) => {
    const { variables } = params || {};
    // const mergedOptions = options ? { ...DEFAULT_OPTIONS, ...options } : DEFAULT_OPTIONS;

    setCalled(true);
    const query = getQuery();
    const queryKey = JSON.stringify(variables);
    const existingQuery = query[queryReferenceKey]?.[queryKey];
    const currentTime = Date.now();

    if (existingQuery?.pending && pollInterval <= 0) {
      console.log('Existing query is pending:', existingQuery, currentTime);
      return;
    }

    setQuery({
      ...query[queryReferenceKey],
      [queryKey]: {
        ...(existingQuery || {}),
        pending: true,
        startTime: currentTime,
      },
    });

    switch (fetchPolicy) {
      case 'cache-first':
        if (existingQuery && !existingQuery.pending && existingQuery.response?.[queryReferenceKey]) {
          console.log('existingQuery', existingQuery);
          const data = existingQuery.response;
          setData(data);
          onCompleted(data);
        } else {
          processRequest(variables);
        }
        break;

      case 'network-only':
        processRequest(variables, onRegisterPolling);
        break;

      case 'cache-only':
        if (existingQuery) {
          const data = existingQuery.response;
          setData(data);
          onCompleted(data);
        } else {
          processRequest(variables);
        }
        break;

      case 'no-cache':
        processRequest(variables);
        break;

      default:
        processRequest(variables);
        break;
    }
  };

  useEffect(() => {
    setQuery({ ...getQuery()[queryReferenceKey], error: null, loading: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [handleQuery, queryResponse];
};

let registeredQueries: any = {};
export const useHttpClient = () => {
  // let cb: any = null;

  const getVariables = (queryKey: string) => {
    return registeredQueries[queryKey]?.variables || {};
  };

  const processRequest = (variables: unknown, queryKey: string) => {
    const controller = new AbortController();
    const URL = SERVICE_NAME_URL_MAPPING[queryKey];
    return httpFetch(
      URL,
      {
        ...DEFAULT_OPTIONS,
        body: variables,
        signal: controller.signal,
        method: MapHttpMethodResponseReferenceKeys[queryKey],
      },
      queryKey,
    );
  };

  const subscribeFn = (cbFn, variables, queryKey) => {
    registeredQueries[queryKey].cb = cbFn;
    // cb = cbFn; // update callback function

    // on subscription dont call the query if variables are not set
    if (Object.keys(variables).length > 0) {
      registeredQueries[queryKey].cb({ data: { getMpnAutoCorrection: null }, loading: true });
      registeredQueries[queryKey].processRequest(variables, queryKey).then((response: unknown) => {
        registeredQueries[queryKey].cb({ data: normalizeHTTPResponse(response, queryKey), loading: false });
      });
    }

    return {
      unsubscribe: () => {
        console.log('unsubscribed', queryKey);
        registeredQueries[queryKey].processRequest = null;
      },
      cancel: () => {
        console.log('cancel', queryKey);
        registeredQueries[queryKey].processRequest = null;
      },
    };
  };

  const setVariablesFn = (variables: any, queryKey: string) => {
    registeredQueries[queryKey].variables = variables;
    registeredQueries[queryKey].cb({ data: { getMpnAutoCorrection: null }, loading: true });

    if (!registeredQueries[queryKey].processRequest) {
      registeredQueries = {
        ...registeredQueries,
        [queryKey]: {
          ...registeredQueries[queryKey],
          processRequest: (variables: unknown) => processRequest(variables, queryKey),
        },
      };
    }

    registeredQueries[queryKey].processRequest(variables).then((response: unknown) => {
      registeredQueries[queryKey].cb({ data: normalizeHTTPResponse(response, queryKey), loading: false });
    });
  };

  const processWatchQueryFn = (queryKey: string, variables: unknown): any => {
    return {
      variables: getVariables(queryKey),
      subscribe: (cbFn) => subscribeFn(cbFn, getVariables(queryKey), queryKey),
      setVariables: (variables: unknown) => setVariablesFn(variables, queryKey),
      processRequest: (variables: unknown) => processRequest(variables, queryKey),
    };
  };

  // support for batch validate serials
  const query = ({ query, variables }): any => {
    const queryKey: string = print(query);
    const URL = SERVICE_NAME_URL_MAPPING[queryKey];
    return httpFetch(URL, {
      ...DEFAULT_OPTIONS,
      body: variables,
    }).then((validateSerialsMetadata: unknown) => ({
      // Remove this reference on Batch API is implemented
      data: {
        validateSerialsMetadata,
      },
    }));
  };

  const watchQuery = ({ query, variables }: any) => {
    const queryKey: string = print(query);
    registeredQueries = {
      ...registeredQueries,
      [queryKey]: processWatchQueryFn(queryKey, variables),
    };

    return registeredQueries[queryKey];
  };

  return {
    watchQuery,
    query,
  };
};

export const useQuerySelector = {
  useHttpQuery: useHttpQuery,
  useLazyQuery: useHttpQuery,
  useMutation: useHttpQuery,
};
